import { ChangeEvent, FC, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  Icon,
  Typography,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { FormNumberField } from 'src/components/FormNumberField/FormNumberField';
import { FormTextField } from 'src/components/FormTextField/FormTextField';
import { RoomAttributes as BaseRoomAttributes } from 'src/components/RoomAttributes/RoomAttributes';
import { Section } from 'src/components/Section/Section';
import { roomRatio } from 'src/pages/AdministratorPage/consts';
import { getAdminState } from 'src/store/admin/selectors';
import { adminActions } from 'src/store/admin/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { Room, RoomType } from 'src/types';

import { CoordsFields } from './CoordsFields/CoordsFields';
import classes from './Fields.module.scss';
import { FontSizeField } from './FontSizeField';
import { TitleField } from './TitleField';
import { WidthField } from './WidthField';

const titleLabelByRoomType: Record<RoomType, string> = {
  [RoomType.Focus]: 'Номер фокус-комнаты',
  [RoomType.Meeting]: 'Номер переговорной',
  [RoomType.Phone]: 'Номер телефонной кабинки',
};

interface FieldsProps {
  bookingObject: Room;
}

const Fields: FC<FieldsProps> = ({ bookingObject }) => {
  const dispatch = useAppDispatch();

  const changeSeatCount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(
        adminActions.update({
          seatCount: e.target.value ? +e.target.value : undefined,
        }),
      );
    },
    [dispatch],
  );

  const titleLabel =
    titleLabelByRoomType[bookingObject.roomType ?? RoomType.Meeting];

  return (
    <Section
      title='Укажите данные ОБ'
      contentClassName={classes.fields}
      testId='administratorRoomData'
    >
      <TitleField label={titleLabel} />

      {bookingObject.roomType === RoomType.Meeting && (
        <FormNumberField
          name='seatCount'
          onChange={changeSeatCount}
          size='medium'
          label='Количество мест'
          fullWidth
          dataTestId='administratorRoomSeatCount'
          startAdornment={<Icon iconName={Icons.User} />}
          min={1}
          positive
          required
        />
      )}
    </Section>
  );
};

interface RoomAttributesProps {
  bookingObject: Room;
}

const RoomAttributes: FC<RoomAttributesProps> = ({ bookingObject }) => {
  const dispatch = useAppDispatch();
  const { placeLoading } = useSelector(getAdminState);

  const changeWithTv = useCallback(
    (checked: boolean) => {
      dispatch(adminActions.update({ withTv: checked }));
    },
    [dispatch],
  );

  const changeWithVideoCalls = useCallback(
    (checked: boolean) => {
      dispatch(
        adminActions.update({
          withVideoCalls: checked,
        }),
      );
    },
    [dispatch],
  );

  const changeWithFlipChart = useCallback(
    (checked: boolean) => {
      dispatch(
        adminActions.update({
          withFlipChart: checked,
        }),
      );
    },
    [dispatch],
  );

  const changeWithWater = useCallback(
    (checked: boolean) => {
      dispatch(
        adminActions.update({
          withWater: checked,
        }),
      );
    },
    [dispatch],
  );

  return (
    <BaseRoomAttributes
      withTv={!!bookingObject.withTv}
      onChangeWithTv={changeWithTv}
      withVideoCalls={!!bookingObject.withVideoCalls}
      onChangeWithVideoCalls={changeWithVideoCalls}
      withFlipChart={!!bookingObject.withFlipChart}
      onChangeWithFlipChart={changeWithFlipChart}
      withWater={!!bookingObject.withWater}
      onChangeWithWater={changeWithWater}
      loading={placeLoading}
    />
  );
};

export interface RoomFieldsProps {
  bookingObject: Room;
}

export const RoomFields: FC<RoomFieldsProps> = ({ bookingObject }) => {
  const dispatch = useAppDispatch();

  const changeEmail = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(adminActions.update({ adEmail: e.target.value }));
    },
    [dispatch],
  );

  return (
    <>
      <Fields bookingObject={bookingObject} />

      <Section
        title='Координаты и размер'
        testId='administratorCoordsAndSize'
        contentClassName={classes.fields}
      >
        <CoordsFields bookingObject={bookingObject} />
        <WidthField ratio={roomRatio} />
        <FontSizeField />
      </Section>

      {bookingObject.roomType === RoomType.Meeting && (
        <div className={classes.attributes}>
          <Typography
            variant='subtitle1'
            data-testid='administratorRoomAttributes'
          >
            Что в себя включает ОБ
          </Typography>
          <RoomAttributes bookingObject={bookingObject} />
        </div>
      )}
      <Section title='Email' testId='administratorEmail'>
        <FormTextField
          name='adEmail'
          onChange={changeEmail}
          size='medium'
          label='Email'
          fullWidth
        />
      </Section>
    </>
  );
};
